import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../config/axiosClient';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/common/SEO';

import EditIcon from '@mui/icons-material/Edit';
import FavoriteIcon from '@mui/icons-material/Favorite';

import Config from "../config/Config.js";
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import ProjectCard from '../components/project/ProjectCard';
import UserCard from '../components/user/UserCard';
import SweetLoadingSpinner from "../components/eyeCandy/SweetLoadingSpinner.js";

const PAGE_SIZE = 20;

const tabStyle = (active) => ({
    fontSize: 14, fontWeight: 600, textDecoration: 'none',
    padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
    border: 'none', background: active ? '#59c6c1' : 'transparent',
    color: active ? '#0f172a' : 'var(--muted)',
    transition: 'all 100ms ease',
});

const subTabStyle = (active) => ({
    fontSize: 13, fontWeight: 600, textDecoration: 'none',
    padding: '3px 10px', borderRadius: 5, cursor: 'pointer',
    border: active ? '1px solid var(--border)' : '1px solid transparent',
    background: active ? 'var(--surface)' : 'transparent',
    color: active ? 'var(--ink)' : 'var(--muted)',
    transition: 'all 100ms ease',
});

const countBadge = {
    fontSize: 12, fontWeight: 600,
    color: 'var(--ink)',
    marginLeft: 6,
    opacity: 0.5,
};

export default function Home() {
    const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
    const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

    const [extendedUser, setExtendedUser] = useState({});
    const [loading, setLoading] = useState(true);
    const [topTab, setTopTab] = useState('projects');
    const [projectTab, setProjectTab] = useState('all');
    const [filter, setFilter] = useState('');

    // Separate state per project tab
    const [privateProjects, setPrivateProjects] = useState([]);
    const [privateCount, setPrivateCount] = useState(0);
    const [privateNext, setPrivateNext] = useState(null);
    const [privateLoading, setPrivateLoading] = useState(true);

    const [publishedProjects, setPublishedProjects] = useState([]);
    const [publishedCount, setPublishedCount] = useState(0);
    const [publishedNext, setPublishedNext] = useState(null);
    const [publishedLoading, setPublishedLoading] = useState(true);

    const [likedProjects, setLikedProjects] = useState([]);
    const [likedCount, setLikedCount] = useState(0);
    const [likedLoading, setLikedLoading] = useState(false);
    const [likedFetched, setLikedFetched] = useState(false);

    const [loadingMore, setLoadingMore] = useState(false);

    // Initial fetch: profile + first page of both tabs
    useEffect(() => {
        async function init() {
            try {
                const [profileRes, privateRes, publishedRes] = await Promise.all([
                    axios.get(`${Config().extendedUserUrl}?user=${user.pk}`).then(res => {
                        const data = res.data;
                        const results = Array.isArray(data) ? data : data.results || [];
                        return results[0] || {};
                    }).catch(() => ({})),
                    axios.get(`${Config().baseUrl}?owner=${user.pk}&publish=false&limit=${PAGE_SIZE}`).catch(() => ({ data: { results: [], count: 0, next: null } })),
                    axios.get(`${Config().baseUrl}?owner=${user.pk}&publish=true&limit=${PAGE_SIZE}`).catch(() => ({ data: { results: [], count: 0, next: null } })),
                ]);
                setExtendedUser(profileRes);
                const privData = privateRes.data || privateRes;
                setPrivateProjects(privData.results || []);
                setPrivateCount(privData.count || 0);
                setPrivateNext(privData.next?.replace('http://', 'https://') || null);
                setPrivateLoading(false);
                const pubData = publishedRes.data || publishedRes;
                setPublishedProjects(pubData.results || []);
                setPublishedCount(pubData.count || 0);
                setPublishedNext(pubData.next?.replace('http://', 'https://') || null);
                setPublishedLoading(false);
            } catch (err) {
                console.error('Home load error:', err);
            } finally {
                setLoading(false);
            }
        }
        init();
    }, [user.pk]);

    // Lazy fetch liked projects when tab is selected
    useEffect(() => {
        if (projectTab !== 'liked' || likedFetched) return;
        setLikedLoading(true);
        axios.get(`${Config().baseUrl}liked/`, {
            headers: { Authorization: `Token ${auth.token}` },
        }).then(res => {
            const projects = Array.isArray(res.data) ? res.data : [];
            setLikedProjects(projects);
            setLikedCount(projects.length);
            setLikedLoading(false);
            setLikedFetched(true);
        }).catch(() => {
            setLikedLoading(false);
            setLikedFetched(true);
        });
    }, [projectTab, likedFetched, auth.token]);

    const loadMore = useCallback(() => {
        const nextUrl = projectTab === 'private' ? privateNext : publishedNext;
        if (!nextUrl || loadingMore) return;
        setLoadingMore(true);
        axios.get(nextUrl)
            .then(res => {
                const results = res.data.results;
                const next = res.data.next?.replace('http://', 'https://') || null;
                if (projectTab === 'private') {
                    setPrivateProjects(prev => [...prev, ...results]);
                    setPrivateNext(next);
                } else {
                    setPublishedProjects(prev => [...prev, ...results]);
                    setPublishedNext(next);
                }
            })
            .catch(err => console.error('Home loadMore error:', err))
            .finally(() => setLoadingMore(false));
    }, [projectTab, privateNext, publishedNext, loadingMore]);

    const q = filter.toLowerCase();
    const matchFilter = (p) => !q ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.subtitle || '').toLowerCase().includes(q) ||
        (p.technology || '').toLowerCase().includes(q) ||
        (p.skill || '').toLowerCase().includes(q);

    const filteredPublished = publishedProjects.filter(matchFilter);
    const filteredPrivate = privateProjects.filter(matchFilter);
    const filteredLiked = likedProjects.filter(matchFilter);

    // For single-tab views (liked)
    const activeProjects = projectTab === 'liked' ? filteredLiked : [];
    const activeTabLoading = projectTab === 'liked' ? likedLoading : false;

    const hasCv = !!(extendedUser?.cv);

    return (
        <div className="container" style={{ paddingTop: '50px' }}>
            <SEO title="Home" url="/home" />
            <div className="row">
                <div className="col-md-3 col-sm-3 col-xs-12">
                    <SweetLoadingSpinner loadingState={loading} />
                    <UserCard user={user} extended_user={extendedUser} settings={true} back_home={false} view_profile={true} />
                </div>
                <div className="col-md-9 col-sm-9">
                    {/* Top-level tabs */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <button type="button" style={tabStyle(topTab === 'projects')} onClick={() => setTopTab('projects')}>
                            Projects
                        </button>
                        {hasCv && (
                            <button type="button" style={tabStyle(topTab === 'cv')} onClick={() => setTopTab('cv')}>
                                CV
                            </button>
                        )}
                    </div>
                    <hr style={{ marginTop: 8 }} />

                    {/* Projects section */}
                    {topTab === 'projects' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                <button type="button" style={subTabStyle(projectTab === 'all')} onClick={() => setProjectTab('all')}>
                                    All
                                    <span style={countBadge}>{publishedCount + privateCount}</span>
                                </button>
                                <button type="button" style={subTabStyle(projectTab === 'liked')} onClick={() => setProjectTab('liked')}>
                                    <FavoriteIcon style={{ fontSize: 12, color: '#ba3f9c', marginRight: 3, verticalAlign: -1 }} />
                                    Liked
                                    {likedFetched && <span style={countBadge}>{likedCount}</span>}
                                </button>
                                <a
                                    href="/projects/new"
                                    style={{
                                        marginLeft: 'auto',
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontSize: 12, color: 'var(--muted)', textDecoration: 'none',
                                    }}
                                >
                                    + New project
                                </a>
                            </div>

                            <input
                                type="text"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter projects..."
                                style={{
                                    width: '100%', padding: '8px 12px', marginBottom: 12,
                                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                                    background: 'var(--surface)', color: 'var(--ink)', fontSize: 14,
                                }}
                            />

                            {projectTab === 'all' ? (
                                <>
                                    {/* Published projects */}
                                    {publishedLoading ? (
                                        <SweetLoadingSpinner loadingState={true} />
                                    ) : (
                                        <>
                                            {filteredPublished.length > 0 && (
                                                <>
                                                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', margin: '0 0 8px' }}>
                                                        Published <span style={{ fontWeight: 400 }}>{publishedCount}</span>
                                                    </p>
                                                    <div className="row">
                                                        {filteredPublished.map(project => (
                                                            <ProjectCard project={project} key={project.id} />
                                                        ))}
                                                    </div>
                                                    {publishedNext && (
                                                        <div className="text-center py-2">
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => {
                                                                    if (loadingMore) return;
                                                                    setLoadingMore(true);
                                                                    axios.get(publishedNext)
                                                                        .then(res => {
                                                                            setPublishedProjects(prev => [...prev, ...res.data.results]);
                                                                            setPublishedNext(res.data.next?.replace('http://', 'https://') || null);
                                                                        })
                                                                        .catch(() => {})
                                                                        .finally(() => setLoadingMore(false));
                                                                }}
                                                                disabled={loadingMore}
                                                            >
                                                                {loadingMore ? 'Loading...' : 'Load more published'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Drafts */}
                                            {!privateLoading && filteredPrivate.length > 0 && (
                                                <>
                                                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', margin: '20px 0 8px' }}>
                                                        Drafts <span style={{ fontWeight: 400 }}>{privateCount}</span>
                                                    </p>
                                                    <div className="row">
                                                        {filteredPrivate.map(project => (
                                                            <ProjectCard project={project} key={project.id} />
                                                        ))}
                                                    </div>
                                                    {privateNext && (
                                                        <div className="text-center py-2">
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => {
                                                                    if (loadingMore) return;
                                                                    setLoadingMore(true);
                                                                    axios.get(privateNext)
                                                                        .then(res => {
                                                                            setPrivateProjects(prev => [...prev, ...res.data.results]);
                                                                            setPrivateNext(res.data.next?.replace('http://', 'https://') || null);
                                                                        })
                                                                        .catch(() => {})
                                                                        .finally(() => setLoadingMore(false));
                                                                }}
                                                                disabled={loadingMore}
                                                            >
                                                                {loadingMore ? 'Loading...' : 'Load more drafts'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {filteredPublished.length === 0 && filteredPrivate.length === 0 && (
                                                <div style={{ color: 'var(--muted)', fontSize: 14, padding: '20px 0' }}>
                                                    {filter
                                                        ? `No projects matching "${filter}".`
                                                        : 'No projects yet. Hit + to start a new project.'
                                                    }
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    {activeTabLoading ? (
                                        <SweetLoadingSpinner loadingState={true} />
                                    ) : activeProjects.length === 0 ? (
                                        <div style={{ color: 'var(--muted)', fontSize: 14, padding: '20px 0' }}>
                                            {filter
                                                ? `No liked projects matching "${filter}".`
                                                : "You haven't liked any projects yet."
                                            }
                                        </div>
                                    ) : (
                                        <div className="row">
                                            {activeProjects.map(project => (
                                                <ProjectCard project={project} key={project.id} />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* CV section */}
                    {topTab === 'cv' && hasCv && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <a
                                    href="/settings/cv"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontSize: 12, color: 'var(--muted)', textDecoration: 'none',
                                    }}
                                >
                                    <EditIcon style={{ fontSize: 14 }} /> Edit CV
                                </a>
                            </div>
                            <div className="custom-body-markdown" style={{ fontSize: 14, lineHeight: 1.7 }}>
                                <ReactMarkdown>{extendedUser.cv}</ReactMarkdown>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
